import { str_convert_currency_to_symbol } from 'helpers';
import get from 'lodash/get';
import { is_gift_checkout_page } from './routes';
import {
    GATEWAY_GOOGLEPLAY,
    GATEWAY_ITUNES,
    GATEWAY_ADYEN,
    FAMILY_PLAN_MEMBERS,
    PATHS,
} from 'config';
import { catalogStore, paymentStore, commonStore } from 'stores';
import { COPY } from 'config/copy';
import { plural, t, defineMessage } from '@lingui/macro';
import { i18n } from '@lingui/core';

export function canUsePaypal(currency = '') {
    const noPaypalCurrencies = [
        'COP',
        'CLP',
        'PEN',
        'CNY',
        'IDR',
        'VND',
        'ZAR',
        'TRY',
        'RON',
        'INR',
        'MYR',
    ];

    return noPaypalCurrencies.indexOf(currency.toUpperCase()) === -1;
}

// Looks like unused code
// Should be removed when we are sure it's not needed for a future feature or something
export function plan_get_discount(plan) {
    console.warn(
        'plan_get_discount is used. Remove the deprecation notice in panels.js',
    );
    const { gateway, core_plan_data } = plan;
    const gate = gateway === 'adyen' ? plan.adyen : core_plan_data;

    const discount = gate.price.discount;
    const discounted = isNaN(discount) ? discount.calculated_price : discount;
    const amount = (discounted / 100).toFixed(2);
    let price = plan_get_price(plan, true);
    // delete price.period

    return {
        amount: price.currency + amount,
        price,
    };
}

export function toFixed(num, fixed) {
    var re = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?');
    return num.toString().match(re)[0];
}

export function plan_get_price(plan, discounted = true, separator = '/') {
    const {
        core_plan_data,
        core_plan_data: { billing_period },
    } = plan;

    const gateway = determineGateway(plan);
    const price = gateway === 'adyen' ? plan.adyen.price : core_plan_data.price;
    const PeriodsList = {
        day: plural(billing_period.number, {
            one: ' day',
            other: `${billing_period.number} days`,
        }),
        week: plural(billing_period.number, {
            one: ' week',
            other: `${billing_period.number} weeks`,
        }),
        month: plural(billing_period.number, {
            one: ' month',
            other: `${billing_period.number} months`,
        }),
        year: plural(billing_period.number, {
            one: ' year',
            other: `${billing_period.number} years`,
        }),
    };

    let total = price.amount;

    if (discounted && price.discount) {
        total = price.discount.calculated_price;
    }

    const amount = (toFixed(total) / 100).toFixed(2);
    const currency = str_convert_currency_to_symbol(price.currency);
    const period = `${separator ? `${separator} ` : ''}${
        PeriodsList[billing_period.unit]
            ? i18n._(PeriodsList[billing_period.unit])
            : billing_period.unit
    }`;

    let display = { amount, period, currency };

    // use "usage" to determine if you need the ready to display formatted string, or the original numeric value
    const preparePerMonthAmount = (total, usage = 'DISPLAY') => {
        let monthsInBillingPeriod = 1;
        if (billing_period.unit === 'year') {
            monthsInBillingPeriod = 12;
        } else if (billing_period.unit === 'month') {
            monthsInBillingPeriod = billing_period.number;
        }
        return usage === 'DISPLAY'
            ? (toFixed(total / monthsInBillingPeriod) / 100).toFixed(2)
            : total / monthsInBillingPeriod;
    };

    /**
     * Need to hard code how we display price when it's yearly because of a
     * requirement (https://yousician.atlassian.net/browse/YS-16367). For now
     * it will show the monthly amount instead.
     */
    if (plan.break_price_to_monthly) {
        display = {
            amount: preparePerMonthAmount(total),
            period: i18n._(t`/ month`),
            currency,
        };
    }

    const data = {
        amount: amount,
        currency: currency,
        period,
        numeric: total,
        numeric_per_month_calculated_in_frontend: preparePerMonthAmount(
            total,
            'NUMERIC',
        ),
    };

    return {
        ...data,
        string: `${data.amount} ${data.period}`,
        display,
    };
}

export function plan_get_charge_period(plan) {
    const price = plan_get_price(plan, null, false);
    return price.period;
}

// To see the full list of plans and periods check out model/plan.py
export function plan_get_prints(plan) {
    const gateway = determineGateway(plan);

    const { plusTaxIsNeeded } = paymentStore;
    // itunes and googleplay don't support this, only for ADYEN subscriptions
    if (gateway !== GATEWAY_ADYEN) return;

    const rawPrice = get(plan, 'adyen.price.discount.calculated_price')
        ? get(plan, 'adyen.price.discount.calculated_price')
        : get(plan, 'adyen.price.amount');
    const price = (toFixed(rawPrice) / 100).toFixed(2);

    const plan_has_discount = (plan) => get(plan, 'adyen.price.discount', 0);
    const tax = defineMessage({
        id: '+ tax',
        message: '+ tax',
    });

    if (plan_is_yearly_monthly(plan)) {
        return i18n._(t`Yearly plan billed monthly`);
    } else if (plan_is_quarterly(plan)) {
        return i18n._(
            t`Billed ${str_convert_currency_to_symbol(
                get(plan, 'adyen.price.currency'),
            )}${price} ${plusTaxIsNeeded ? i18n._(tax) : ''} every 3 months`,
        );
    } else if (plan_is_monthly(plan)) {
        return i18n._(
            t`Billed ${str_convert_currency_to_symbol(
                get(plan, 'adyen.price.currency'),
            )}${price} ${
                plusTaxIsNeeded ? i18n._(tax) : ''
            } monthly, cancel anytime`,
        );
    } else if (plan_is_yearly(plan)) {
        const tailDiscount = defineMessage({
            id: 'for the first year',
            message: 'for the first year',
        });

        const tailYear = defineMessage({
            id: '/ year',
            message: '/ year',
        });

        return i18n._(
            t`Billed ${str_convert_currency_to_symbol(
                get(plan, 'adyen.price.currency'),
            )}${price}  ${plusTaxIsNeeded ? i18n._(tax) : ''} ${
                plan_has_discount(plan)
                    ? i18n._(tailDiscount)
                    : i18n._(tailYear)
            } `,
        );
    } else {
        // If a plan hits this else, it means the plan is not added to frontend.
        return '';
    }
}

// yearly plan, billed once per year
export function plan_is_yearly(plan) {
    const { billing_period, subscription_period } = plan.core_plan_data;
    return (
        billing_period.number === 1 &&
        billing_period.unit === 'year' &&
        subscription_period.number === 1 &&
        subscription_period.unit === 'year'
    );
}

// billed each month, commitment one month
function plan_is_monthly(plan) {
    const { billing_period, subscription_period } = plan.core_plan_data;
    return (
        billing_period.number === 1 &&
        billing_period.unit === 'month' &&
        subscription_period.number === 1 &&
        subscription_period.unit === 'month'
    );
}

// a plan with a one year commitment, but billed each month
export function plan_is_yearly_monthly(plan) {
    const { billing_period, subscription_period } = plan.core_plan_data;

    return (
        billing_period.number === 1 &&
        billing_period.unit === 'month' &&
        subscription_period.number === 1 &&
        subscription_period.unit === 'year'
    );
}

// a plan with a 3 month commitment, but billed each 3 months
export function plan_is_quarterly(plan) {
    const { billing_period, subscription_period } = plan.core_plan_data;

    return (
        billing_period.number === 3 &&
        billing_period.unit === 'month' &&
        subscription_period.number === 3 &&
        subscription_period.unit === 'month'
    );
}

export function plan_get_name(plan, userPolicies, fullName = false) {
    if (userPolicies && !userPolicies.licensed_content) {
        let name = !plan.core_plan_data.features.all_instruments
            ? t`One instrument`
            : t`All instruments`;

        if (fullName) {
            name = t`Premium ${name}`;
        }

        return name;
    }

    const licensed = plan_is_licensed(plan);
    const family = plan_is_family(plan);
    return family
        ? t`Premium+ Family`
        : licensed
        ? t`Premium+ Personal`
        : t`Premium`;
}

export function plan_is_licensed(plan) {
    return plan.core_plan_data.features.licensed_content;
}

export function plan_is_family(plan) {
    return plan.core_plan_data.features.family;
}

export function plan_sanitize_gateway(gateway) {
    return gateway.replace('ios', 'itunes').replace('none', 'adyen');
}

export function plan_get_type(plan) {
    if (plan_is_family(plan)) return 'family';
    if (plan_is_licensed(plan)) return 'licensed';
    if (plan.core_plan_data.features.all_instruments) return 'all';

    return 'premium';
}

export function plan_get_remaining_value(plan) {
    const gateway = determineGateway(plan);
    const data = plan[plan_sanitize_gateway(gateway)];
    const defaultEmptyRemaining = { amount: 0, charged: 0, discount: 0 };
    const remaining = data.remaining || defaultEmptyRemaining;

    return {
        amount: (toFixed(remaining.amount) / 100).toFixed(2),
        charged:
            remaining.charged < 0
                ? '0.00'
                : (toFixed(remaining.charged) / 100).toFixed(2),
        discount: remaining.discount,
    };
}
// get gateway based on which one is not null
// since sept 2019 the gateway field is not reliable anymore and returns "unkown" sometimes
function determineGateway(plan) {
    if (plan[GATEWAY_GOOGLEPLAY]) {
        return GATEWAY_GOOGLEPLAY;
    } else if (plan[GATEWAY_ITUNES]) {
        return GATEWAY_ITUNES;
    } else {
        return GATEWAY_ADYEN;
    }
}
export function plan_has_free_trial(plan) {
    const { core_plan_id } = plan;
    const gateway = determineGateway(plan);
    const data = plan[plan_sanitize_gateway(gateway)];

    return data.free_trial_available && core_plan_id.indexOf('yearly') >= 0;
}

export function plan_get_campaign_features(plan, userPolicies) {
    const { core_plan_data } = plan;
    const licensed = plan_is_licensed(plan);
    const family = plan_is_family(plan);

    const features = [
        {
            name: i18n._(t`Unlock all songs`),
            available: licensed,
        },
        {
            name: core_plan_data.features.all_instruments
                ? i18n._(t`All instruments`)
                : i18n._(t`One instrument`),
            available: true,
        },
        {
            name: i18n._(t`Unlimited lesson time`),
            available: true,
        },
    ];

    if (licensed) {
        family
            ? features.splice(2, 0, {
                  name: i18n._(t`${FAMILY_PLAN_MEMBERS} Premium+ accounts`),
                  available: true,
              })
            : features.splice(2, 0, {
                  name: i18n._(t`1 Premium+ account`),
                  available: true,
              });
    }

    if (userPolicies && !userPolicies.licensed_content) {
        features.shift();
        features.push({
            name: i18n._(t`Play anytime, anywhere`),
            available: true,
        });
    }

    return features;
}

export function plan_get_bf_features(plan) {
    const family = plan_is_family(plan);
    return [
        {
            name: family
                ? i18n._(
                      t`Personal progress tracking for <b>4 family members</b>`,
                  )
                : i18n._(t`Personal progress tracking`),
            available: true,
        },
        {
            name: i18n._(t`Unlimited lesson time`),
            available: true,
        },
        {
            name: i18n._(t`10,000+ lessons & popular songs`),
            available: true,
        },
        {
            name: i18n._(t`Access to all 5 instruments`),
            available: true,
        },
    ];
}

export function plan_get_features(plan, userPolicies) {
    const { core_plan_data } = plan;
    const licensed = plan_is_licensed(plan);
    const family = plan_is_family(plan);
    const is_yearly_monthly = plan_is_yearly_monthly(plan);

    let features = [];

    if (licensed && family) {
        // Family plans
        features = [
            {
                name: i18n._(t`${FAMILY_PLAN_MEMBERS} Premium+ accounts`),
                available: true,
            },
            {
                name: i18n._(t`Unlimited lesson time`),
                available: true,
            },
            {
                name: i18n._(t`10,000+ lessons & popular songs`),
                available: true,
            },
            {
                name: i18n._(t`Access all 5 instruments`),
                available: true,
            },
            {
                name: i18n._(
                    t`Personal progress tracking for each family member`,
                ),
                available: true,
            },
        ];
    } else if (licensed && !family) {
        // Licensed plans that are not family plans
        features = [
            is_yearly_monthly
                ? {
                      name: i18n._(t`Lesson plan for all instruments`),
                      available: true,
                  }
                : {
                      name: i18n._(t`1 Premium+ account`),
                      available: true,
                  },
            {
                name: i18n._(t`Unlimited lesson time`),
                available: true,
            },
            {
                name: i18n._(t`10,000+ lessons & popular songs`),
                available: true,
            },
        ];
        if (
            userPolicies &&
            userPolicies.licensed_content &&
            !is_yearly_monthly
        ) {
            features.push({
                name: i18n._(t`Access all 5 instruments`),
                available: true,
            });
        }

        features.push({
            name: i18n._(t`Personal progress tracking`),
            available: true,
        });
    } else if (!licensed && !family) {
        // Premium and Premium one instrument plans
        if (!core_plan_data.features.all_instruments) {
            features = [
                (userPolicies && !userPolicies.licensed_content) ||
                is_yearly_monthly
                    ? {
                          name: i18n._(
                              t`Lessons for 1 instrument of your choice`,
                          ),
                          available: true,
                      }
                    : {
                          name: i18n._(t`1 Premium account`),
                          available: true,
                      },
                {
                    name: i18n._(t`Unlimited lesson time`),
                    available: true,
                },
            ];
            if (
                userPolicies &&
                userPolicies.licensed_content &&
                !is_yearly_monthly
            ) {
                features.push({
                    name: i18n._(t`Songs by Yousician`),
                    available: true,
                });
                features.push({
                    name: i18n._(t`1 instrument of your choise`),
                    available: true,
                });
            }
        } else {
            // Premium All instruments
            features = [
                {
                    name: i18n._(t`Lessons for all 5 instruments`),
                    available: true,
                },
                {
                    name: i18n._(t`Unlimited lesson time`),
                    available: true,
                },
            ];
        }

        features.push({
            name: i18n._(t`Personal progress tracking`),
            available: true,
        });
    }

    return features;
}

export function plan_get_free_trial_duration(plan) {
    const gateway = determineGateway(plan);
    return plan[plan_sanitize_gateway(gateway)].free_trial_duration.number;
}

export function plan_get_payment_features(plan, userPolicies) {
    const hasFreeTrial = plan_has_free_trial(plan);
    let features = plan_get_features(plan, userPolicies);

    features.unshift({
        name:
            plan_is_yearly(plan) || plan_is_yearly_monthly(plan)
                ? i18n._(t`12-month membership`)
                : plan_is_quarterly(plan)
                ? i18n._(t`3-month membership`)
                : i18n._(t`1-month membership`),
        available: true,
    });

    if (hasFreeTrial) {
        const freeTrialDuration = plan_get_free_trial_duration(plan);

        features.unshift({
            name: i18n._(
                t`${freeTrialDuration}-day free trial. Cancel anytime.`,
            ),
            available: true,
        });
    }

    return features;
}

const GIFTING_PLANS = {
    monthly_licensed: {
        headline: t`Beginners`,
        title: t`1 month`,
    },
    quarterly_licensed: {
        headline: t`Casual`,
        title: t`3 months`,
    },
    semiannual_licensed: {
        headline: t`Casual`,
        title: t`6 months`,
    },
    yearly_licensed: {
        headline: t`Dedicated`,
        title: t`12 months`,
    },
};

/**
 * Return the correct plan title
 * for gifting plans
 *
 * @param {object} plan
 */
export function plan_gifting_titles(plan) {
    if (GIFTING_PLANS[plan.id]) {
        return GIFTING_PLANS[plan.id];
    }

    return {
        title: plan.name,
        headline: '',
    };
}

export function plan_get_discount_help(plan) {
    const period = plan_is_yearly_monthly(plan)
        ? 'year'
        : plan.core_plan_data.billing_period.unit;

    return COPY.DISCOUNT_DISCLAIMER_PERIODS[period]
        ? i18n._(COPY.DISCOUNT_DISCLAIMER_PERIODS[period])
        : '';
}

/**
 * Return the information for this plan
 * mainly used in checkout pages.
 *
 * @param {object} plan
 */
export function plan_get_info(plan, fullprice = null) {
    const { core_plan_data } = plan;
    paymentStore.setPlusTaxIsNeeded({ plan });
    const { plusTaxIsNeeded } = paymentStore;
    const price = plan_get_price(plan);
    const free_trial = plan_has_free_trial(plan);
    const info = [];
    // FIXME: Not sure if this will work properly after a language change
    const discountHelp = plan_get_discount_help(plan);
    const tax = defineMessage({
        id: '+ tax',
        message: '+ tax',
    });

    info.push({
        label: t`Instrument`,
        value: core_plan_data.features.all_instruments
            ? t`All instruments`
            : t`One instrument`,
    });

    if (fullprice) {
        info.push({
            filter: 'overview',
            strike: true,
            label: t`Original price`,
            value: `${fullprice.currency}${fullprice.amount} ${
                plusTaxIsNeeded ? i18n._(tax) : ''
            } ${fullprice.period}`,
        });
    }

    if (free_trial) {
        info.push({
            filter: 'overview',
            label: t`Amount charged now`,
            value: `${price.currency}0.00`,
        });

        let label = i18n._(
            t`${price.currency}${price.amount} ${
                plusTaxIsNeeded ? i18n._(tax) : ''
            } ${i18n._(price.period)} after trial`,
        );

        if (fullprice) {
            label += `. ${discountHelp}`;
        }
        info.push({
            label: label,
            help_icon: true,
        });
    } else {
        info.push({
            filter: 'overview|subscription',
            label: t`Subscription`,
            value: i18n._(
                t`${price.currency}${price.amount} ${
                    plusTaxIsNeeded ? i18n._(tax) : ''
                } ${i18n._(price.period)}`,
            ),
        });

        if (fullprice) {
            info.push({
                label: discountHelp,
            });
        }
    }

    return info;
}

export const plan_billing_name = (billingPeriod) => {
    const getBillingName = (key) =>
        ({
            'week-1': t`Weekly`,
            'month-1': t`Monthly`,
            'month-3': t`Quarterly`,
            'month-6': t`Semiannual`,
            'year-1': t`Yearly`,
        }[key]);

    return getBillingName(`${billingPeriod.unit}-${billingPeriod.number}`);
};

/**
 * Return the billing cycle for a given
 * billing period.
 *
 * @param {object} period
 */
export function plan_get_billing_cycle(period, billingPeriod) {
    if (
        period !== billingPeriod &&
        billingPeriod.unit === 'month' &&
        period.unit === 'year'
    ) {
        return t`Yearly plan billed monthly`;
    }

    return i18n._(plan_billing_name(billingPeriod));
}

export function plan_get_billing_terms(plan) {
    let terms = '';

    if (plan.is_yearly_monthly) {
        terms = i18n._(
            t`Your contract will renew automatically on your annual renewal date, until you cancel your subscription. Note that cancelling your subscription will not result in a refund for your remaining contract obligation, but you will not be charged for the next annual contract period.`,
        );
    } else if (is_gift_checkout_page()) {
        terms = '';
    } else {
        terms = i18n._(
            t`Your contract will renew automatically on your ${i18n
                ._(plan.billing_type)
                .toLowerCase()} renewal date, until you cancel your subscription.`,
        );
    }

    return terms;
}

export function plan_get_billing_info(plan) {
    const { plusTaxIsNeeded } = paymentStore;
    let terms = '';
    let amount = plan.price.amount;

    const tax = defineMessage({
        id: '+ tax',
        message: '+ tax',
    });

    const chargePeriod = defineMessage({
        id: plan.charge_period,
        message: plan.charge_period,
    });

    if (plan.is_yearly_monthly) {
        terms = i18n._(
            t`By subscribing to this plan you agree that you will be <strong>charged automatically ${
                plan.price.currency
            }${amount} ${plusTaxIsNeeded ? i18n._(tax) : ''} every ${i18n._(
                chargePeriod,
            )} for the duration of your yearly contract</strong>.`,
        );
    } else if (plan.discount) {
        if (plan.free_trial) {
            terms = i18n._(t`You will not be charged for your free trial.`);
        }
        terms += ' ';
        terms += plan_get_discount_help(plan.source);
        terms += ' ';
        terms += i18n._(
            t`After that, your card will automatically be charged <strong>${
                plan.price.currency
            }${plan.fullprice.amount} ${
                plusTaxIsNeeded ? i18n._(tax) : ''
            } every ${i18n._(chargePeriod)}.</strong>`,
        );
    } else if (plan.free_trial) {
        terms = i18n._(
            t`After the free trial, your card will automatically be charged <strong>${
                plan.price.currency
            }${amount} ${plusTaxIsNeeded ? i18n._(tax) : ''} every ${i18n._(
                chargePeriod,
            )}.</strong>`,
        );
    } else if (is_gift_checkout_page()) {
        terms = i18n._(t`This is a one-time payment.`);
    } else {
        terms = i18n._(
            t`Your card will automatically be charged <strong>${
                plan.price.currency
            }${amount} ${plusTaxIsNeeded ? i18n._(tax) : ''} every ${i18n._(
                chargePeriod,
            )}.</strong>`,
        );
    }

    terms += ` ${plan_get_billing_terms(plan)}`;

    return terms;
}

export function plan_get_trial_terms(plan) {
    const { plusTaxIsNeeded } = paymentStore;

    let amount = plan.price.amount;

    const tax = defineMessage({
        id: '+ tax',
        message: '+ tax',
    });

    const chargePeriod = defineMessage({
        id: plan.charge_period,
        message: plan.charge_period,
    });

    return plan.free_trial
        ? i18n._(
              t`After your free trial, you'll auto-enroll in our <strong>${
                  plan.price.currency
              }${amount} ${plusTaxIsNeeded ? i18n._(tax) : ''} / ${i18n._(
                  chargePeriod,
              )}</strong> plan. <br> Change or <strong>cancel anytime</strong> during your trial to not be charged.`,
          )
        : null;
}

export function plan_get_description(plan) {
    const {
        userPolicies,
        userLocation: { country },
    } = commonStore;

    const isPremiumTitle =
        (userPolicies && userPolicies.licensed_content) ||
        !(userPolicies || ['JP', 'AR'].includes(country));

    return plan.is_family_plan
        ? {
              title: i18n._(t`Premium+ <span>Family</span>`),
              subtitle: i18n._(t`Best value`),
          }
        : plan.licensed
        ? {
              title: i18n._(t`Premium+ <span>Personal</span>`),
              subtitle: i18n._(t`Most popular`),
          }
        : plan.has_all_instruments
        ? {
              title: i18n._(t`All instruments`),
              subtitle: i18n._(t`Most popular`),
          }
        : {
              title: isPremiumTitle
                  ? i18n._(t`Premium`)
                  : i18n._(t`One instrument`),
              subtitle: i18n._(t`The basics`),
          };
}

export function plan_decorate(plan, userPolicies) {
    const {
        core_plan_id,
        core_plan_data,
        core_plan_data: { billing_period },
    } = plan;

    const price = plan_get_price(plan);
    const fullprice = plan_get_price(plan, false);
    const discount =
        fullprice.amount !== price.amount
            ? `${Math.round(
                  ((fullprice.amount - price.amount) * 100) / fullprice.amount,
              )}`
            : null;

    const data = {
        id: core_plan_id,
        billing_type: plan_billing_name(billing_period),
        billing_cycle: plan_get_billing_cycle(billing_period, billing_period),
        billing: billing_period,
        free_trial: plan_has_free_trial(plan),
        name: plan_get_name(plan, userPolicies, true),
        licensed: plan_is_licensed(plan),
        family: plan_is_family(plan),
        features: plan_get_features(plan, userPolicies),
        campaign_features: plan_get_campaign_features(plan, userPolicies),
        payment_features: plan_get_payment_features(plan, userPolicies),
        is_yearly_monthly: plan_is_yearly_monthly(plan),
        price: price,
        fullprice: discount ? fullprice : null,
        discount: discount,
        info: plan_get_info(plan, discount ? fullprice : null),
        prints: plan_get_prints(plan),
        charge_period: plan_get_charge_period(plan),
        has_all_instruments: core_plan_data.features.all_instruments,
        remaining: plan_get_remaining_value(plan),
        is_family_plan: core_plan_id.includes('family'),
        source: plan,
        bf_features: plan_get_bf_features(plan),
    };

    return data;
}

export function get_plans_and_redirect(subscription, coupon, type) {
    const plans = subscription
        ? subscription.is_upgradable
            ? catalogStore.upgrade()
            : subscription.can_resubscribe
            ? catalogStore.resubscribe()
            : null
        : catalogStore.subscribe({ coupon: coupon });
    const redirectTo = subscription
        ? subscription.is_upgradable
            ? PATHS.upgradeCheckout + '/overview'
            : subscription.can_resubscribe
            ? PATHS.resubscribeCheckout + '/overview'
            : null
        : type === 'premium'
        ? PATHS.subscribeCheckout + '/instruments'
        : PATHS.subscribeCheckout + '/overview';

    return {
        plans,
        redirectTo,
    };
}

/*
 * Gateway helpers.
 * Remember that gatweay also returns unknown and admin
 */

// detect if user is subscribed via itunes by checking the gateway
export const isItunes = (gateway) => gateway === GATEWAY_ITUNES;

// detect if user is subscribed via google
export const isGoogleplay = (gateway) => gateway === GATEWAY_GOOGLEPLAY;

export const isItunesOrGoogleplay = (gateway) =>
    isItunes(gateway) || isGoogleplay(gateway);

export const isAdyen = (gateway) => gateway === GATEWAY_ADYEN;

export const get_plan_type = (plan) => {
    return plan.family
        ? 'family'
        : plan.licensed || (!plan.licensed && plan.has_all_instruments)
        ? 'premiumPlus'
        : 'premium';
};

export const PlanTypes = {
    free: { name: t`Free`, mobileDisplay: t`Free` },
    premium: { name: t`Premium`, mobileDisplay: t`P` },
    premium_plus: {
        name: t`Premium+`,
        mobileDisplay: t`P+`,
    },
    premium_plus_family: {
        name: t`Premium+ Family`,
        mobileDisplay: t`Family`,
    },
};

export const PlanFeatures = [
    {
        name: t`Guided learning path`,
        availableFor: [
            PlanTypes.free,
            PlanTypes.premium,
            PlanTypes.premium_plus,
            PlanTypes.premium_plus_family,
        ],
    },
    {
        name: t`Access across your devices`,
        availableFor: [
            PlanTypes.free,
            PlanTypes.premium,
            PlanTypes.premium_plus,
            PlanTypes.premium_plus_family,
        ],
    },
    {
        name: t`Lessons for all levels`,
        availableFor: [
            PlanTypes.premium,
            PlanTypes.premium_plus,
            PlanTypes.premium_plus_family,
        ],
    },

    {
        name: t`Fun weekly challenges`,
        availableFor: [
            PlanTypes.premium,
            PlanTypes.premium_plus,
            PlanTypes.premium_plus_family,
        ],
    },
    {
        name: t`Unlimited lesson time with progress tracking`,
        availableFor: [
            PlanTypes.premium,
            PlanTypes.premium_plus,
            PlanTypes.premium_plus_family,
        ],
    },
    {
        name: t`Access to learn all 5 instruments`,
        availableFor: [PlanTypes.premium_plus, PlanTypes.premium_plus_family],
    },
    {
        name: t`10,000+ lessons & popular songs`,
        availableFor: [PlanTypes.premium_plus, PlanTypes.premium_plus_family],
    },
    {
        name: t`A personal account for 4 family members`,
        availableFor: [PlanTypes.premium_plus_family],
    },
    {
        name: t`Personal progress tracking for each family member`,
        availableFor: [PlanTypes.premium_plus_family],
    },
];
